import { type ReactNode, useCallback } from "react";

import { Drawer, type DrawerProps } from "antd";
import { useTracking } from "react-tracking";

import { mobile, useMobileBackHandler } from "@btnet/utilities-webview";
import { NativeBackCloseProvider } from "@btnet/utilities-webview/NativeBackCloseContext";

/**
 * core-components typechecks with `exactOptionalPropertyTypes`, under which an optional
 * prop does not accept an explicit `undefined`. Callers forwarding a possibly-absent value
 * (`onClose={props.onClose}`) are the normal case for a drawer, so each picked prop admits
 * `undefined` alongside its own type.
 */
type WithExplicitUndefined<T> = { [K in keyof T]: T[K] | undefined };

export interface IBTDrawerProps
  extends WithExplicitUndefined<
    Pick<
      DrawerProps,
      | "className"
      | "placement"
      | "visible"
      | "afterVisibleChange"
      | "forceRender"
      | "width"
      | "height"
      | "closable"
      | "mask"
      | "title"
      | "footer"
      | "destroyOnClose"
      | "keyboard"
      | "bodyStyle"
      | "push"
      | "getContainer"
      | "style"
      | "zIndex"
      | "contentWrapperStyle"
      | "drawerStyle"
    >
  > {
  children: ReactNode;
  role?: React.AriaRole;
  /** Accessible name for the drawer, needed when it has no visible title of its own. */
  "aria-label"?: string;
  /**
   * antd types the close event as required, but a native back press has no DOM event
   * to pass. Narrowing it to optional lets the WebView back path call onClose() with no
   * argument without a cast; antd's own close paths still pass the event. The explicit
   * `| undefined` matches the picked props: callers forward a possibly-absent handler.
   */
  onClose?: ((e?: React.MouseEvent | React.KeyboardEvent) => void) | undefined;
}

export function BTDrawer({
  children,
  visible,
  afterVisibleChange,
  closable,
  onClose,
  ...otherProps
}: IBTDrawerProps) {
  const { trackEvent, Track } = useTracking<Record<string, unknown>>({
    element: "Drawer",
  });

  // In the WebView, native back closes any open BTDrawer regardless of its size or shape.
  // Claim back only when the drawer can actually close; without an onClose, claiming would
  // suppress native's default back and then no-op.
  const canClaimBack = visible === true && onClose !== undefined;

  // Whether native back has taken over closing — drops antd's header X and drives the
  // provider.
  const isNativeBackReplacingClose = mobile.canClaimNativeBack(canClaimBack);

  // Route native back to onClose while this drawer is open and topmost. Gated on the same
  // flag that hides the close control, so a handler is registered exactly when back has taken
  // over closing — never on desktop or an older build. Safe to call unconditionally: the hook
  // no-ops outside the WebView.
  useMobileBackHandler(() => onClose?.(), isNativeBackReplacingClose);

  const handleVisibleChange = useCallback(() => {
    if (visible) {
      trackEvent({
        event: "DrawerOpen",
      });
    } else {
      trackEvent({
        event: "DrawerClose",
      });
    }
    afterVisibleChange?.(visible ? visible : false);
  }, [visible, trackEvent, afterVisibleChange]);

  return (
    <Track>
      {/*
       * The provider wraps the whole Drawer — header and body — so any close control
       * inside it (antd's built-in X via `closable`, an in-body X, or a consumer's custom
       * header button) can drop itself once native back has taken over closing.
       */}
      <NativeBackCloseProvider
        isNativeBackReplacingClose={isNativeBackReplacingClose}
      >
        <Drawer
          afterVisibleChange={handleVisibleChange}
          visible={visible ?? false}
          // The cast only re-narrows what WithExplicitUndefined widened: every key here comes
          // from DrawerProps, and antd reads an explicitly-undefined prop the same as an absent
          // one. It is not covering a shape mismatch. onClose/closable are cast alongside so
          // an explicit `undefined` is accepted under exactOptionalPropertyTypes.
          {...({
            ...otherProps,
            onClose,
            closable: isNativeBackReplacingClose ? false : closable,
          } as DrawerProps)}
        >
          {children}
        </Drawer>
      </NativeBackCloseProvider>
    </Track>
  );
}
